import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, FileText, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportExport({ reportType = "leads", buttonVariant = "outline" }) {
  const [exporting, setExporting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({});

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/reports/export`,
        {
          report_type: reportType,
          format: format,
          filters: Object.keys(filters).length > 0 ? filters : null
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report exported successfully!`);
      setShowModal(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    } finally {
      setExporting(null);
    }
  };

  const reportLabels = {
    properties: "Properties",
    leads: "Leads",
    users: "Users/Team",
    search_analytics: "Search Analytics"
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={() => setShowModal(true)}
        className="gap-2"
        data-testid={`export-${reportType}-btn`}
      >
        <Download className="h-4 w-4" />
        Export {reportLabels[reportType] || reportType}
      </Button>

      {/* Export Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0f2744]">
                Export {reportLabels[reportType] || reportType} Report
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-6">
              Choose your preferred format to download the report.
            </p>

            {/* Filter options for properties */}
            {reportType === "properties" && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-600 mb-2 block">Optional Filters</Label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={filters.property_type || ""}
                    onChange={(e) => setFilters({ ...filters, property_type: e.target.value || undefined })}
                    className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="land">Land</option>
                  </select>
                  <select
                    value={filters.purpose || ""}
                    onChange={(e) => setFilters({ ...filters, purpose: e.target.value || undefined })}
                    className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                  >
                    <option value="">All Purposes</option>
                    <option value="sale">Sale</option>
                    <option value="rent">Rent</option>
                    <option value="lease">Lease</option>
                  </select>
                </div>
              </div>
            )}

            {/* Filter options for leads */}
            {reportType === "leads" && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-600 mb-2 block">Optional Filters</Label>
                <select
                  value={filters.status || ""}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                  className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            )}

            {/* Filter options for users */}
            {reportType === "users" && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-600 mb-2 block">Optional Filters</Label>
                <select
                  value={filters.role || ""}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value || undefined })}
                  className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="team_leader">Team Leader</option>
                  <option value="agent">Agent</option>
                  <option value="sourcing_manager">Sourcing Manager</option>
                  <option value="user">Property Seeker</option>
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => exportReport("excel")}
                disabled={exporting}
                variant="outline"
                className="h-24 flex-col gap-2 border-2 hover:border-green-500 hover:bg-green-50"
                data-testid="export-excel-btn"
              >
                {exporting === "excel" ? (
                  <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                ) : (
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                )}
                <span className="text-sm font-medium">Excel (.xlsx)</span>
              </Button>

              <Button
                onClick={() => exportReport("pdf")}
                disabled={exporting}
                variant="outline"
                className="h-24 flex-col gap-2 border-2 hover:border-red-500 hover:bg-red-50"
                data-testid="export-pdf-btn"
              >
                {exporting === "pdf" ? (
                  <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                ) : (
                  <FileText className="h-8 w-8 text-red-600" />
                )}
                <span className="text-sm font-medium">PDF (.pdf)</span>
              </Button>
            </div>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Reports include all data matching the selected filters
            </p>
          </div>
        </div>
      )}
    </>
  );
}
